/**
 * The unsaved-work guard's decisions, apart from React so the runner can execute them (#591).
 */

export interface Pending {
  go: () => void;
}

export interface GuardState {
  /** Whether a holder has declared work that would be lost right now. */
  dirty: boolean;
  /** The way out waiting on the question, if one is being asked. */
  pending: Pending | null;
}

/** A transition, and the way out to run once it is applied (null: run nothing). */
export interface Step {
  state: GuardState;
  run: (() => void) | null;
}

export const initialGuard: GuardState = { dirty: false, pending: null };

/** A holder saying whether it holds unsaved work. */
export function declare(state: GuardState, dirty: boolean): GuardState {
  return state.dirty === dirty ? state : { ...state, dirty };
}

/** A way out: taken at once when nothing would be lost, otherwise held while the question is asked. */
export function askToLeave(state: GuardState, go: () => void): Step {
  return state.dirty ? { state: { ...state, pending: { go } }, run: null } : { state, run: go };
}

/** Keep editing, and the question's own close button. */
export function keepEditing(state: GuardState): GuardState {
  return { ...state, pending: null };
}

/**
 * Discard changes: the way out that was asked about is taken.
 *
 * The flag is left to the holder. Taking the way out unmounts it, and its `useUnsavedWork` cleanup clears the flag;
 * a way out that leaves it mounted (a role switch keeps an editor) leaves its draft on screen, and that draft is still
 * guarded. Clearing the flag here instead let the next way out drop the draft without asking (#591): a holder's effect
 * only runs again when its own answer changes, so nothing would ever set the flag back.
 */
export function discardChanges(state: GuardState): Step {
  return { state: { ...state, pending: null }, run: state.pending?.go ?? null };
}
